/**
 * Conduite — sélection d'un étudiant puis vue historique + saisie d'entrée.
 */
import { useMemo, useState } from 'react';
import {
  Heart, Loader2, Search, X, Plus, ThumbsUp, ThumbsDown, User,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useMesEtudiantsQuery, useConduiteEtudiantFormateurQuery,
  useCreerConduiteFormateurMutation, useFormateurMeQuery,
  type EtudiantItem,
} from '../../services/formateurApi';

export default function Conduite() {
  const { data: me } = useFormateurMeQuery();
  const [classe, setClasse] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selEtu, setSelEtu] = useState<EtudiantItem | null>(null);

  const { data, isLoading } = useMesEtudiantsQuery(classe ? { classe } : undefined);
  const filtres = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.results || []).filter(e =>
      !q || e.nom.toLowerCase().includes(q) || e.prenoms.toLowerCase().includes(q)
        || e.matricule.toLowerCase().includes(q)
    );
  }, [data, search]);

  if (selEtu) {
    return <VueConduiteEtudiant etudiant={selEtu} onBack={() => setSelEtu(null)} />;
  }

  return (
    <FormateurLayout title="Conduite étudiants">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-2.5 mb-3 space-y-2">
        {/* Filtres classe */}
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setClasse('')}
            className={`px-2 py-1 text-[11px] font-semibold rounded ${
              classe === '' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'
            }`}>
            Toutes
          </button>
          {(me?.classes || []).map(c => (
            <button key={c.code} onClick={() => setClasse(c.code)}
              className={`px-2 py-1 text-[11px] font-semibold rounded ${
                classe === c.code ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300'
              }`}>
              {c.code}
            </button>
          ))}
        </div>
        {/* Recherche */}
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Nom, prénom, matricule…"
            className="w-full pl-7 pr-2 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded text-gray-100" />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement…
        </div>
      ) : filtres.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-sm">Aucun étudiant trouvé.</div>
      ) : (
        <div className="space-y-1">
          {filtres.map(e => (
            <button key={e.matricule} onClick={() => setSelEtu(e)}
              className="w-full bg-gray-900 border border-gray-800 hover:border-amber-700/60 rounded-lg p-2 flex items-center gap-2 text-left transition">
              <div className="w-8 h-8 rounded-full bg-gray-800 text-gray-400 flex items-center justify-center">
                <User size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white truncate">
                  {e.prenoms} {e.nom}
                </div>
                <div className="text-[10px] text-gray-500 truncate">
                  {e.classe} • {e.matricule}
                </div>
              </div>
              <Heart size={12} className="text-gray-600" />
            </button>
          ))}
        </div>
      )}
    </FormateurLayout>
  );
}

function VueConduiteEtudiant({ etudiant, onBack }: {
  etudiant: EtudiantItem; onBack: () => void;
}) {
  const { data, isLoading, refetch } = useConduiteEtudiantFormateurQuery(etudiant.matricule);
  const [showNouvelle, setShowNouvelle] = useState(false);

  return (
    <FormateurLayout title={`${etudiant.prenoms} ${etudiant.nom}`}>
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-900/30 border border-amber-700/60 text-amber-400 flex items-center justify-center">
          <Heart size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-white">{etudiant.prenoms} {etudiant.nom}</div>
          <div className="text-[11px] text-gray-400">{etudiant.classe} • {etudiant.matricule}</div>
        </div>
        <button onClick={onBack} className="text-[11px] text-gray-400 hover:text-white px-2 py-1">
          ← Retour
        </button>
      </div>

      <button onClick={() => setShowNouvelle(true)}
        className="w-full mb-3 bg-gradient-to-br from-blue-900/40 to-blue-900/10 hover:border-blue-500
          border border-blue-700/40 rounded-xl p-3 flex items-center gap-3 transition-colors text-left">
        <div className="w-9 h-9 rounded-lg bg-black/30 flex items-center justify-center text-blue-300">
          <Plus size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Nouvelle observation</div>
          <div className="text-[11px] text-gray-400">Positive ou négative — pour la conduite</div>
        </div>
      </button>

      <h2 className="text-[11px] uppercase tracking-wider font-bold text-gray-400 mb-2">
        Historique ({data?.total || 0})
      </h2>

      {isLoading ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
        </div>
      ) : (data?.items || []).length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm">Aucune observation enregistrée.</div>
      ) : (
        <div className="space-y-1.5">
          {data!.items.map(it => {
            const pos = it.type === 'positif';
            return (
              <div key={it.id} className={`rounded-lg border p-2.5 ${
                pos ? 'bg-emerald-900/20 border-emerald-800/60'
                    : 'bg-red-900/20 border-red-800/60'
              }`}>
                <div className="flex items-start gap-2">
                  <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 ${
                    pos ? 'bg-emerald-900/40 text-emerald-300' : 'bg-red-900/40 text-red-300'
                  }`}>
                    {pos ? <ThumbsUp size={12} /> : <ThumbsDown size={12} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white">{it.commentaire}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {it.categorie_label} • Poids {it.poids} •{' '}
                      {new Date(it.date_observation).toLocaleDateString('fr-FR')} •{' '}
                      {it.auteur_nom}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNouvelle && (
        <ModalNouvelle matricule={etudiant.matricule}
          onClose={() => setShowNouvelle(false)}
          onDone={() => { setShowNouvelle(false); refetch(); }} />
      )}
    </FormateurLayout>
  );
}

function ModalNouvelle({ matricule, onClose, onDone }: {
  matricule: string; onClose: () => void; onDone: () => void;
}) {
  const [type, setType] = useState<'positif' | 'negatif'>('positif');
  const [categorie, setCategorie] = useState('comportement');
  const [poids, setPoids] = useState(1);
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState('');
  const [creer, { isLoading }] = useCreerConduiteFormateurMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!commentaire.trim()) { setErreur('Commentaire requis'); return; }
    try {
      await creer({ matricule, type, categorie, poids, commentaire }).unwrap();
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.error || err?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-base text-white">Nouvelle observation</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setType('positif')}
            className={`px-3 py-2 text-xs rounded border inline-flex items-center justify-center gap-1.5 ${
              type === 'positif'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-gray-800 text-gray-300 border-gray-700'
            }`}>
            <ThumbsUp size={12} /> Positive
          </button>
          <button type="button" onClick={() => setType('negatif')}
            className={`px-3 py-2 text-xs rounded border inline-flex items-center justify-center gap-1.5 ${
              type === 'negatif'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-gray-800 text-gray-300 border-gray-700'
            }`}>
            <ThumbsDown size={12} /> Négative
          </button>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">Catégorie</label>
          <select value={categorie} onChange={(e) => setCategorie(e.target.value)}
            className="w-full px-2 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded text-gray-100">
            <option value="comportement">Comportement</option>
            <option value="participation">Participation</option>
            <option value="assiduité">Assiduité</option>
            <option value="travail">Travail</option>
            <option value="autre">Autre</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">
            Importance : <span className="text-amber-300">{poids}</span> / 10
          </label>
          <input type="range" min={1} max={10} value={poids} onChange={(e) => setPoids(Number(e.target.value))}
            className="w-full accent-amber-500" />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-300 mb-1">Commentaire *</label>
          <textarea value={commentaire} onChange={(e) => setCommentaire(e.target.value)}
            rows={4} required maxLength={2000}
            placeholder="Décrivez l'observation…"
            className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 resize-none" />
        </div>

        {erreur && (
          <div className="bg-red-900/30 border border-red-800 text-red-200 rounded p-2 text-xs">
            {erreur}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !commentaire.trim()}
            className={`px-4 py-1.5 text-xs font-semibold text-white rounded inline-flex items-center gap-1.5 ${
              type === 'positif' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
            } disabled:opacity-50`}>
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}
